import { useState } from 'react';
import { Box, FormControl, MenuItem, Pagination, Select, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface TablePaginationControlProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export function TablePaginationControl({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
}: TablePaginationControlProps) {
  const { t } = useTranslation();
  const totalPages = Math.ceil(count / rowsPerPage);

  if (count === 0) return null;

  const startItem = (page - 1) * rowsPerPage + 1;
  const endItem = Math.min(page * rowsPerPage, count);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        py: 2,
        px: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {t('pagination.rowsPerPage', 'Linhas por página:')}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              onRowsPerPageChange(Number(e.target.value));
              onPageChange(1);
            }}
            sx={{
              '& .MuiSelect-select': {
                py: 0.75,
                pr: 3,
              },
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t('pagination.showing', '{{start}}-{{end}} de {{total}}', {
            start: startItem,
            end: endItem,
            total: count,
          })}
        </Typography>
      </Box>

      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, newPage) => onPageChange(newPage)}
          color="primary"
          shape="rounded"
          size="small"
          showFirstButton
          showLastButton
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: 1.5,
            },
          }}
        />
      )}
    </Box>
  );
}

export function usePagination(defaultRowsPerPage = 10) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const paginateData = <T,>(data: T[]): T[] => {
    const startIndex = (page - 1) * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  };

  const resetPage = () => setPage(1);

  return {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    paginateData,
    resetPage,
  };
}
